/**
 * Button interaction state utilities
 * These functions help create consistent hover, active, and transition states for buttons
 */
import { transition } from "./transitions";

// Common transition class used across all button variants
const TRANSITION_BASE = transition({ duration: 200 });

/**
 * Creates hover and active state classes for buttons
 * @param hoverBg - Background color for hover state (e.g., "bg-cede-primary-dark")
 * @param activeBg - Background color for active state (e.g., "bg-cede-primary")
 * @param shadow - Shadow class for hover state (e.g., "shadow-lg", "shadow-md", "shadow-sm")
 * @param additionalHover - Additional hover classes (e.g., "hover:text-cede-primary-dark")
 * @param additionalActive - Additional active classes (optional)
 * @returns Combined class string with hover, active, and transition states
 */
export const createButtonInteractions = ({
  hoverBg,
  activeBg,
  shadow = "shadow-lg",
  additionalHover = "",
  additionalActive = "",
}: {
  hoverBg: string;
  activeBg?: string;
  shadow?: "shadow-lg" | "shadow-md" | "shadow-sm" | "";
  additionalHover?: string;
  additionalActive?: string;
}) => {
  const hoverClasses = [hoverBg, shadow && `hover:${shadow}`, additionalHover].filter(Boolean).join(" ");

  const activeClasses = [activeBg && `active:${activeBg}`, "active:scale-[0.98]", additionalActive]
    .filter(Boolean)
    .join(" ");

  return `${hoverClasses} ${activeClasses} ${TRANSITION_BASE}`.trim();
};

/**
 * Predefined interaction patterns for common button variants
 */
export const buttonInteractionPatterns = {
  /** Main button interactions - lighter on hover, darker on active */
  main: () =>
    createButtonInteractions({
      hoverBg: "hover:bg-cede-button-primary-hover",
      activeBg: "bg-cede-button-primary-active",
      shadow: "shadow-lg",
    }),

  /** Primary button interactions - lighter on hover, darker on active */
  primary: () =>
    createButtonInteractions({
      hoverBg: "hover:bg-cede-button-primary-hover",
      activeBg: "bg-cede-button-primary-active",
      shadow: "shadow-lg",
    }),

  /** Secondary button interactions - more opacity on hover, medium shadow */
  secondary: () =>
    createButtonInteractions({
      hoverBg: "hover:bg-cede-button-primary/40",
      activeBg: "bg-cede-button-primary/50",
      shadow: "shadow-md",
    }),

  /** Tertiary button interactions - same as primary */
  tertiary: () =>
    createButtonInteractions({
      hoverBg: "hover:bg-cede-button-primary-hover",
      activeBg: "bg-cede-button-primary-active",
      shadow: "shadow-lg",
    }),

  /** Transparent button interactions - subtle background on hover */
  transparent: () =>
    createButtonInteractions({
      hoverBg: "hover:bg-cede-primary/10",
      activeBg: "bg-cede-primary/20",
      shadow: "",
      additionalHover: "hover:text-cede-primary-dark",
    }),

  /** Transparent with border button interactions - background + border changes */
  transparentBordered: () =>
    createButtonInteractions({
      hoverBg: "hover:bg-cede-primary/10",
      activeBg: "bg-cede-primary/20",
      shadow: "",
      additionalHover: "hover:text-cede-primary-dark hover:border-cede-primary-dark",
    }),

  /** Destructive button interactions - error colors */
  destructive: () =>
    createButtonInteractions({
      hoverBg: "hover:bg-cede-error/50",
      activeBg: "bg-cede-error",
      shadow: "shadow-lg",
    }),

  /** Ghost button interactions - component background on hover */
  ghost: () =>
    createButtonInteractions({
      hoverBg: "hover:bg-cede-bg-component-hover",
      activeBg: "bg-cede-bg-component",
      shadow: "shadow-sm",
    }),

  /** Link button interactions - text color changes only */
  link: () =>
    createButtonInteractions({
      hoverBg: "",
      shadow: "",
      additionalHover: "hover:underline hover:text-cede-primary-dark",
      additionalActive: "active:text-cede-primary",
    }),

  /** Outline button interactions - border color changes */
  outline: () =>
    createButtonInteractions({
      hoverBg: "",
      activeBg: "",
      shadow: "",
      additionalHover: "hover:border-cede-primary-dark",
    }),

  /** Body text button interactions - text color changes only */
  bodyText: () =>
    createButtonInteractions({
      hoverBg: "",
      activeBg: "",
      shadow: "",
      additionalHover: "hover:text-cede-text-primary",
    }),

  /** Unlink button interactions - darker shade background on hover and active */
  unlink: () =>
    createButtonInteractions({
      hoverBg: "hover:bg-[var(--color-contrast-shade-primary-30)]",
      activeBg: "bg-[var(--color-contrast-shade-primary-36)]",
      shadow: "shadow-md",
    }),
} as const;
